import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Icon,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import moment from "moment";
import React from "react";
import {
  getPresentationStatusColor,
  getPresentationStatusIcon,
} from "../../common/VisualElementsHelper";

const formatDate = (value) =>
  value ? moment(value).format("DD.MM.YYYY HH:mm:ss") : "N/A";

const InfoField = ({ label, value, md = 6 }) => (
  <Grid item xs={12} md={md}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ fontWeight: 500, wordBreak: "break-all" }}
    >
      {value !== null && value !== undefined && value !== "" ? value : "N/A"}
    </Typography>
  </Grid>
);

export default function PresentationDialog({
  open,
  onClose,
  presentation,
  loading,
}) {
  const response = presentation?.zadnjiOdgovor;
  const errors = response?.parsedData?.errors || [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Box>
            <Box display="flex" alignItems="center" gap={1}>
              <span style={{ fontSize: 18, fontWeight: 600 }}>
                Presentation details
              </span>
            </Box>
          </Box>
          <Box flexGrow={1} />
          <IconButton onClick={onClose} size="small">
            <Icon>close</Icon>
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        {loading && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            py={5}
            gap={1}
          >
            <CircularProgress size={20} />
            <span>Loading presentation…</span>
          </Box>
        )}

        {!loading && !presentation && (
          <Box textAlign="center" py={5} color="text.secondary">
            No presentation found for this declaration.
          </Box>
        )}

        {!loading && presentation && (
          <>
            {errors.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Response errors
                  </Typography>
                  <Chip label={errors.length} size="small" color="error" />
                </Box>
                <Grid container spacing={1.5}>
                  {errors.map((error, index) => (
                    <Grid
                      item
                      xs={12}
                      key={error?.id ? error?.id : `${error?.code}-${index}`}
                    >
                      <Alert severity="error" sx={{ borderRadius: 2 }}>
                        <AlertTitle sx={{ fontWeight: 600 }}>
                          {error?.code}
                        </AlertTitle>
                        {error?.description}
                        {error?.pointer && (
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, mt: 0.5 }}
                          >
                            Pointer: {error?.pointer}
                          </Typography>
                        )}
                        {error?.originalValue &&
                          error?.originalValue !== error?.description && (
                            <Box
                              sx={{
                                mt: 0.5,
                                px: 1,
                                py: 0.25,
                                display: "inline-block",
                                borderRadius: 1,
                                fontWeight: 600,
                                fontSize: 13,
                                backgroundColor: (theme) =>
                                  alpha(theme.palette.error.main, 0.15),
                              }}
                            >
                              Value: {error?.originalValue}
                            </Box>
                          )}
                      </Alert>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            )}

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                General details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Box>
                    {presentation?.status ? (
                      <Chip
                        label={presentation?.status}
                        size="small"
                        icon={
                          <Icon fontSize="small">
                            {getPresentationStatusIcon(presentation?.status)}
                          </Icon>
                        }
                        color={getPresentationStatusColor(
                          presentation?.status,
                        )}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        N/A
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <InfoField
                  label="Date created"
                  value={formatDate(presentation?.dateCreated)}
                />
                <InfoField
                  label="Date modified"
                  value={formatDate(presentation?.dateModified)}
                />
                <InfoField label="LRN" value={presentation?.lrn} />
                <InfoField label="MRN" value={presentation?.mrn} />
                <InfoField
                  label="MRN source"
                  value={presentation?.mrnSource}
                />
              </Grid>
            </Paper>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} startIcon={<Icon>close</Icon>} variant="text">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
